import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import { analyticsUiTokens as T } from '../../constants/userAnalytics';

const buttonBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  px: 1.75,
  height: 36,
  borderRadius: '8px',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0 1px 2px rgba(15,23,42,0.05)',
  transition: 'background-color 0.15s',
  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  '&:focus-visible': { outline: `2px solid ${T.navy}`, outlineOffset: 2 },
};

/**
 * Export (Excel only) / Refresh. Export works from the numbers already on screen, so it is
 * disabled (with the reason as a tooltip) whenever those numbers are loading, updating or stale.
 */
const AnalyticsHeaderActions = ({ onExportXlsx, onRefresh, refreshing, disabledReason }) => {
  const refreshLabel = useLocalText('Refresh data');
  const timeZoneNote = useLocalText('All dates and day boundaries are US Eastern');
  const disabled = Boolean(disabledReason);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1, sm: 1.25 } }}>
      <Tooltip title={timeZoneNote} arrow enterTouchDelay={0}>
        <IconButton size="small" aria-label={timeZoneNote} sx={{ color: T.slate[500], '&:hover': { color: T.navy } }}>
          <InfoOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Tooltip title={disabledReason ? <LocalText>{disabledReason}</LocalText> : ''} arrow>
        <span>
          <Box
            component="button"
            type="button"
            data-testid="export-xlsx"
            onClick={onExportXlsx}
            disabled={disabled}
            sx={{ ...buttonBase, bgcolor: T.navy, color: '#ffffff', border: 0, '&:hover:not(:disabled)': { bgcolor: T.darkNavy } }}
          >
            <FileDownloadOutlinedIcon sx={{ fontSize: 16, color: T.sky[300] }} />
            <LocalText>Export to Excel</LocalText>
          </Box>
        </span>
      </Tooltip>

      <Tooltip title={<LocalText>Refresh data</LocalText>} arrow>
        <span>
          <Box
            component="button"
            type="button"
            aria-label={refreshLabel}
            onClick={onRefresh}
            disabled={refreshing}
            sx={{
              ...buttonBase, width: 36, px: 0, justifyContent: 'center', bgcolor: '#ffffff', color: T.slate[600], border: `1px solid ${T.slate[200]}`,
              '&:hover:not(:disabled)': { bgcolor: T.slate[50], color: T.navy },
              '@keyframes ua-spin': { to: { transform: 'rotate(360deg)' } },
            }}
          >
            <RefreshOutlinedIcon sx={{ fontSize: 18, animation: refreshing ? 'ua-spin 0.8s linear infinite' : 'none' }} />
          </Box>
        </span>
      </Tooltip>
    </Box>
  );
};

export default AnalyticsHeaderActions;
